import { Menu } from "@ark-ui/solid";
import { type ComponentProps, createMemo, For, type JSX, Match, mergeProps, Show, Switch } from "solid-js";
import type { IconButtonProps } from "../button/Button";
import { IconButton } from "../button/Button";
import { Portal } from "solid-js/web";
import { narrow } from "../../utils/solidUtils";
import { A } from "@solidjs/router";
import { Icon } from "../icon/Icon";
import { useTranslation } from "../../state/i18n";
import type { ListItemHtmlProps, SelectOption } from "../../types/Menu";
import IconListItem from "../menu/listItems/IconListItem";
import DynamicValueListItem from "../menu/listItems/DynamicValueListItem";
import TextListItem from "../menu/listItems/TextListItem";
import type { Placement } from "@floating-ui/utils";
import { isEmpty, isNil, negate } from "lodash-es";

export type ListItemMenuItem<T> =
  | ({
      type: "link";
      href: string;
      target?: "_blank";
      onClick?: () => void;
    } & SelectOption<T>)
  | ({
      type: "action";
      onClick: () => void;
    } & SelectOption<T>);

// A non-interactive section header that can be placed between menu items.
export type ListItemMenuLabel = { type: "label"; label: string };

// Groups of menu items; each group is separated by a divider and may contain labels.
export type ListItemMenuItems<T> = (ListItemMenuItem<T> | ListItemMenuLabel)[][];

export type ListItemMenuSize = "lg" | "md";
type InternalListItemMenuProps<T> = {
  trigger: (menuTriggerProps: Record<string, unknown>) => JSX.Element;
  items: ListItemMenuItems<T>;
  menu: {
    size: ListItemMenuSize;
    positioning: {
      placement: Placement;
    };
  };
};

const ListItemContent = <T,>(props: { item: ListItemMenuItem<T>; htmlProps: ListItemHtmlProps | null }) => (
  <Switch>
    <Match
      when={narrow(
        () => props.item,
        (i) => i.variant === "text",
      )}
    >
      {(i) => <TextListItem htmlProps={props.htmlProps} {...i()} />}
    </Match>
    <Match
      when={narrow(
        () => props.item,
        (i) => i.variant === "icon",
      )}
    >
      {(i) => <IconListItem htmlProps={props.htmlProps} {...i()} />}
    </Match>
    <Match
      when={narrow(
        () => props.item,
        (i) => i.variant === "dynamicValue",
      )}
    >
      {(i) => <DynamicValueListItem htmlProps={props.htmlProps} {...i()} />}
    </Match>
  </Switch>
);

const MenuListItem = <T,>(props: { menuItem: ListItemMenuItem<T> | ListItemMenuLabel }) => (
  <Switch>
    <Match
      when={narrow(
        () => props.menuItem,
        (v) => v.type === "label",
      )}
    >
      {(item) => <div class="px-3 pt-2.5 pb-0.5 text-header-sm font-medium text-secondary">{item().label}</div>}
    </Match>
    <Match
      when={narrow(
        () => props.menuItem,
        (v) => v.type === "link",
      )}
    >
      {(item) => (
        <Menu.Item
          value={item().value as string}
          asChild={(arkProps) => (
            <A href={item().href} target={item().target} onClick={item().onClick} {...arkProps()} class="group">
              <ListItemContent item={item()} htmlProps={null} />
            </A>
          )}
        />
      )}
    </Match>
    <Match
      when={narrow(
        () => props.menuItem,
        (v) => v.type === "action",
      )}
    >
      {(item) => (
        <Menu.Item
          value={item().value as string}
          asChild={(arkProps) => <ListItemContent item={item()} htmlProps={arkProps()} />}
        />
      )}
    </Match>
  </Switch>
);

export const ListItemMenu = <T,>(props: InternalListItemMenuProps<T>) => (
  <Menu.Root
    positioning={{ placement: props.menu.positioning.placement }}
    closeOnSelect
    onSelect={({ value }) => {
      const item = props.items.flat().find((item) => item.type !== "label" && item.value === value);

      if (!isNil(item) && item.type !== "label" && !isNil(item.onClick)) {
        item.onClick();
      }
    }}
    // Prevents the menu from closing when nested inside a focus-trapped component (e.g., Inside a Subform Drawer)
    onFocusOutside={(e) => e.preventDefault()}
  >
    <Menu.Trigger asChild={props.trigger as (props: unknown) => JSX.Element} />
    <Menu.Context>
      {(context) => (
        <Show when={context().open}>
          <Portal>
            <Menu.Positioner>
              <Menu.Content
                class="z-10 -my-1 divide-y divide-subtle overflow-y-auto rounded-sm bg-surface-default p-1 shadow-elevation-light-3 outline-none"
                classList={{
                  "max-h-86 max-w-170 min-w-66": props.menu.size === "lg",
                  "max-h-86 max-w-64 min-w-42": props.menu.size === "md",
                }}
              >
                <For each={props.items.filter(negate(isEmpty))}>
                  {(group) => (
                    <div>
                      <For each={group}>{(menuItem) => <MenuListItem menuItem={menuItem} />}</For>
                    </div>
                  )}
                </For>
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Show>
      )}
    </Menu.Context>
  </Menu.Root>
);

export type IconButtonMenuProps<T> = {
  triggerIcon?: IconButtonProps["icon"];
  triggerSize?: IconButtonProps["size"];
  triggerVariant?: IconButtonProps["variant"];
  items: ListItemMenuItems<T>;
  "aria-label": string;
  disabled?: boolean;
  menu?: {
    positioning: {
      placement: Placement;
    };
  };
};

export const IconButtonMenu = <T,>(props: IconButtonMenuProps<T>) => {
  const merged = mergeProps(
    {
      triggerIcon: "dotsVertical" as const,
      triggerSize: "medium" as const,
      triggerVariant: "tertiary" as const,
      disabled: false,
      menu: {
        positioning: {
          placement: "bottom-end" as Placement,
        },
      },
    },
    props,
  );

  return (
    <Show
      when={!merged.disabled}
      fallback={
        <IconButton
          icon={merged.triggerIcon}
          aria-label={merged["aria-label"]}
          variant={merged.triggerVariant}
          size={merged.triggerSize}
          disabled
        />
      }
    >
      <ListItemMenu
        {...merged}
        menu={{
          size: "md",
          positioning: {
            placement: merged.menu.positioning.placement,
          },
        }}
        trigger={(menuTriggerProps) => (
          <IconButton
            {...menuTriggerProps}
            icon={merged.triggerIcon}
            aria-label={merged["aria-label"]}
            variant={merged.triggerVariant}
            size={merged.triggerSize}
          />
        )}
      />
    </Show>
  );
};

export type SplitButtonProps<T> = {
  label: string;
  loading: boolean;
  loadingLabel?: string;
  status: "none" | "success" | "warning" | "danger" | "neutral";
  items: ListItemMenuItems<T>;
  actionButtonProps: ComponentProps<"button">;
  menuButtonProps: ComponentProps<"button">;
};

export const SplitButtonMenu = <T,>(props: SplitButtonProps<T>) => {
  const { t } = useTranslation();

  const statusIndicatorAriaLabel = createMemo(() => {
    switch (props.status) {
      case "success":
        return t(($) => $.STATUS_INDICATION_SUCCESS);
      case "warning":
        return t(($) => $.STATUS_INDICATION_WARNING);
      case "danger":
        return t(($) => $.STATUS_INDICATION_DANGER);
      case "neutral":
        return t(($) => $.STATUS_INDICATION_NEUTRAL);
      case "none":
        return "";
    }
  });

  return (
    <ListItemMenu
      menu={{
        size: "md",
        positioning: {
          placement: "bottom-end",
        },
      }}
      items={props.items}
      trigger={(menuTriggerProps) => (
        <div role="group" aria-label={props.label} class="flex text-primary">
          <button
            disabled={props.loading}
            class="flex h-7.5 items-center gap-1.5 rounded-l border-y border-l border-default bg-button-bg-secondary-default px-2.5 text-button-small text-nowrap select-none hover:bg-button-bg-secondary-hover focus-visible:outline-default active:bg-button-bg-secondary-pressed disabled:pointer-events-none disabled:bg-button-bg-secondary-disabled disabled:text-button-text-secondary-disabled"
            {...props.actionButtonProps}
          >
            <Show when={props.loading}>
              <Icon
                name="spinner"
                size="sm"
                type="solid"
                classList={{
                  "animate-spin content-center": true,
                }}
              />
            </Show>
            <Show when={props.status !== "none" && !props.loading}>
              <svg
                aria-label={statusIndicatorAriaLabel()}
                width="8"
                height="8"
                viewBox="0 0 8 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                classList={{
                  "icon-status-success": props.status === "success",
                  "icon-status-warning": props.status === "warning",
                  "icon-status-danger": props.status === "danger",
                  "icon-status-neutral": props.status === "neutral",
                }}
              >
                <circle cx="4" cy="4" r="4" fill="currentColor" />
              </svg>
            </Show>
            <Show when={props.loading} fallback={props.label}>
              {props.loadingLabel}
            </Show>
          </button>
          <button
            {...props.menuButtonProps}
            {...menuTriggerProps}
            disabled={props.loading}
            class="size-7.5 shrink-0 rounded-r border border-button-border-secondary bg-button-bg-secondary-default hover:bg-button-bg-secondary-hover focus-visible:outline-default active:bg-button-bg-secondary-pressed disabled:pointer-events-none disabled:bg-button-bg-secondary-disabled"
          >
            <Icon
              name="chevronDown"
              size="md"
              type="solid"
              class="mx-auto"
              classList={{ "icon-button-icon-secondary-disabled": props.loading }}
            />
          </button>
        </div>
      )}
    />
  );
};
